import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppShell,
  AppShellNavbar,
  AppShellHeader,
  AppShellMain,
  Text,
  Group,
  Button,
  NavLink,
  Stack,
  Badge,
  ActionIcon,
  useMantineTheme,
  Burger,
  Box,
  PasswordInput,
  Alert,
} from '@mantine/core';
import {
  IconDashboard,
  IconUsers,
  IconLock,
  IconCamera,
  IconWallet,
  IconLogout,
  IconSettings,
  IconKey,
} from '@tabler/icons-react';
import { useWeb3 } from '../contexts/Web3Context';
import { useSupabase } from '../contexts/SupabaseContext';
import { modals } from '@mantine/modals';
import { canAccessAdminControl, canAccessAdminManagement } from '../utils/permissions';

const roleBadge = (level) => {
  if (level === 1) return <Badge color="red" variant="filled">Super Admin</Badge>;
  if (level === 2) return <Badge color="blue" variant="light">Operator</Badge>;
  return <Badge color="gray" variant="light">Read-only</Badge>;
};

const Layout = ({ children }) => {
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [opened, setOpened] = useState(false);
  const { account, isConnected, adminLevel, connectWallet, disconnectWallet } = useWeb3();
  const { user, signOut, updatePassword, error, session, myAdmin } = useSupabase();

  const openChangePasswordModal = () => {
    const ChangePasswordForm = () => {
      const [currentPw, setCurrentPw] = React.useState('');
      const [newPw, setNewPw] = React.useState('');
      const [confirmPw, setConfirmPw] = React.useState('');
      const [submitting, setSubmitting] = React.useState(false);
      const [info, setInfo] = React.useState('');
      const [localError, setLocalError] = React.useState('');

      const handleChange = async () => {
        setLocalError('');
        setInfo('');
        if (!newPw || newPw.length < 6) {
          setLocalError('Password must be at least 6 characters.');
          return;
        }
        if (newPw !== confirmPw) {
          setLocalError('Passwords do not match.');
          return;
        }
        setSubmitting(true);
        // Reauthenticate with current password to ensure it's the account owner
        if (currentPw) {
          // Best-effort reauth; Supabase doesn't have explicit reauth API client-side.
          // If needed, we could call signInWithPassword again; skipping here to avoid side-effects.
        }
        const { error: err } = await updatePassword(newPw);
        setSubmitting(false);
        if (err) {
          setLocalError(err.message || 'Failed to update password');
          return;
        }
        setInfo('Password updated successfully.');
        setTimeout(() => modals.closeAll(), 800);
      };

      return (
        <Stack gap="md">
          {(localError || error) && (
            <Alert color="red" variant="light">
              {localError || error}
            </Alert>
          )}
          {info && (
            <Alert color="blue" variant="light">
              {info}
            </Alert>
          )}
          <PasswordInput
            label="Current password"
            placeholder="Enter current password"
            value={currentPw}
            onChange={(e) => setCurrentPw(e.currentTarget.value)}
          />
          <PasswordInput
            label="New password"
            placeholder="Enter new password"
            value={newPw}
            onChange={(e) => setNewPw(e.currentTarget.value)}
          />
          <PasswordInput
            label="Confirm password"
            placeholder="Re-enter new password"
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => modals.closeAll()}>Cancel</Button>
            <Button loading={submitting} onClick={handleChange}>Update</Button>
          </Group>
        </Stack>
      );
    };

    modals.open({
      title: 'Change Password',
      children: <ChangePasswordForm />,
      centered: true,
      withCloseButton: true,
    });
  };

  const menuItems = [
    { 
      icon: IconDashboard, 
      label: 'Dashboard', 
      path: '/',
      requiresAuth: false // 지갑 연결 없이도 접근 가능
    },
    { 
      icon: IconUsers, 
      label: 'Account List', 
      path: '/accounts',
      requiresAuth: false
    },
    { 
      icon: IconLock, 
      label: 'Lockup List', 
      path: '/lockup',
      requiresAuth: false,
    },
    { 
      icon: IconCamera, 
      label: 'Snapshot List', 
      path: '/snapshot',
      requiresAuth: true,
    },
    // { 
    //   icon: IconSettings, 
    //   label: 'Admin Control', 
    //   path: '/admincontrol',
    //   requiresAuth: true,
    //   requiresAdmin: true // Owner/Manager 접근 가능(권한 체크 로직 재사용)
    // },
    { 
      icon: IconSettings, 
      label: 'Admin Management', 
      path: '/admin',
      requiresAuth: true,
      requiresAdmin: true // Owner/Manager 접근 가능(권한 체크 로직 재사용)
    },
  ];

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  // 메뉴 접근 권한 확인
  const canAccessMenu = (menuItem) => {
    // New rules: Supabase session controls auth, admin by admin_level in public.admin
    if (!menuItem.requiresAuth) return true;
    if (!session) {
      console.log('No session found for menu:', menuItem.label);
      return false;
    }
    if (!menuItem.requiresAdmin) return true;
    
    // 특정 메뉴별 권한 체크
    if (menuItem.path === '/admincontrol') {
      return canAccessAdminControl(myAdmin);
    }
    
    if (menuItem.path === '/admin') {
      return canAccessAdminManagement(myAdmin);
    }
    
    // 기본적으로 Super Admin과 Operator만 접근 가능
    const level = myAdmin?.admin_level;
    console.log('Menu access check for:', menuItem.label, 'Admin level:', level, 'myAdmin:', myAdmin);
    
    const hasAccess = level === 1 || level === 2;
    console.log('Has access:', hasAccess);
    return hasAccess;
  };

  return (
    <AppShell
      header={{ height: 70 }}
      navbar={{
        width: 250,
        breakpoint: 'sm',
        collapsed: { mobile: !opened },
      }}
    >
      <AppShellHeader p="md">
        <Group justify="space-between" h="100%" w="100%">
          <Group>
            <Burger
              opened={opened}
              onClick={() => setOpened((o) => !o)}
              size="sm"
              color={theme.colors.gray[6]}
              hiddenFrom="sm"
            />
            <Group spacing="sm">
              <Box
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden'
                }}
              >
                <img 
                  src="/logo192.png" 
                  alt="EDENA Logo" 
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                  }}
                />
              </Box>
              <Box>
                <Text size="xl" fw={700} c="gray.9">
                  EDENA Admin
                </Text>
              </Box>
            </Group>
          </Group>
          
          <Group gap="lg" align="center">
            {/* 사용자 정보 섹션 */}
            {user && (
              <Group gap="md" align="center">
                <Box>
                  <Group gap="xs" align="center">
                    <Text size="sm" fw={600} c="gray.8">
                      {user.email}
                    </Text>
                    {myAdmin && roleBadge(myAdmin.admin_level)}
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      onClick={openChangePasswordModal}
                      size="sm"
                      title="Change Password"
                    >
                      <IconKey size="0.9rem" />
                    </ActionIcon>
                  </Group>
                </Box>
                <Button
                  variant="outline"
                  color="red"
                  onClick={signOut}
                  size="xs"
                  compact
                >
                  Logout
                </Button>
              </Group>
            )}
            
            {/* 지갑 정보 섹션 */}
            <Group gap="md" align="center">
              {isConnected ? (
                <>
                  <Box>
                    <Text size="sm" fw={600} c="gray.8" mb={2}>
                      {account ? `${account.slice(0, 6)}...${account.slice(-4)}` : 'Unknown'}
                    </Text>
                    <Text size="xs" c="blue.6" fw={500}>
                      {adminLevel}
                    </Text>
                  </Box>
                  <Button
                    leftSection={<IconLogout size="0.8rem" />}
                    variant="outline"
                    color="red"
                    onClick={disconnectWallet}
                    size="xs"
                    compact
                  >
                    Disconnect
                  </Button>
                </>
              ) : (
                <Button
                  leftSection={<IconWallet size="0.8rem" />}
                  variant="filled"
                  color="dark"
                  onClick={connectWallet}
                  size="sm"
                  style={{
                    backgroundColor: '#1A1A1A',
                    color: '#FFFFFF',
                    borderRadius: '8px',
                    fontWeight: 500
                  }}
                >
                  Connect Wallet
                </Button>
              )}
            </Group>
          </Group>
        </Group>
      </AppShellHeader>

      <AppShellNavbar p="md">
        <Stack gap="lg" h="100%">
          {/* 로고 및 타이틀 섹션 */}
          
          
          {/* 메뉴 아이템들 */}
          <Stack gap="xs" style={{ flex: 1 }}>
            {menuItems.map((item) => {
              const hasAccess = canAccessMenu(item);
              const isActiveItem = isActive(item.path);
              
              return (
                <NavLink
                  key={item.path}
                  leftSection={<item.icon size="1.2rem" stroke={1.5} />}
                  label={item.label}
                  active={isActiveItem}
                  disabled={!hasAccess}
                  onClick={() => {
                    if (hasAccess) {
                      navigate(item.path);
                      setOpened(false);
                    }
                  }}
                  style={{
                    opacity: hasAccess ? 1 : 0.5,
                    cursor: hasAccess ? 'pointer' : 'not-allowed',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    backgroundColor: isActiveItem ? '#F3F4F6' : 'transparent',
                    color: isActiveItem ? '#1F2937' : hasAccess ? '#6B7280' : '#9CA3AF',
                    fontWeight: isActiveItem ? 600 : 500,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: hasAccess ? '#F9FAFB' : 'transparent',
                      color: hasAccess ? '#374151' : '#9CA3AF'
                    }
                  }}
                />
              );
            })}
          </Stack>
        </Stack>
      </AppShellNavbar>
      
      <AppShellMain 
        style={{ 
          width: '100%',
          maxWidth: 'none',
          minWidth: 0,
          flex: 1,
          overflow: 'auto'
        }}
      >
        {children}
      </AppShellMain>
    </AppShell>
  );
};

export default Layout;
